import React, { FC, useState, useEffect } from 'react';
import './AITab.scss';
import { BaseText } from 'src/components/typography';
import Button from 'src/components/button';
import { showSuccessToast, showErrorToast } from 'src/components/toast/Toast';
import { getAIConversationsAction, getAiConversationAction, getAIConversationsByMessageAction } from 'src/stores/clients/clients-actions';
import { IAIConversation } from 'src/interfaces/clients-interface';
import { TGetAIConversationsResponse } from 'src/interfaces/clients-interface';
import { Spin, Tabs, Modal, Card, Empty, Table, Space, Tag, Tooltip, DatePicker } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { RangePickerProps } from 'antd/es/date-picker';
import { useAppDispatch } from 'src/stores';
import { Checkbox } from 'antd';
import { summariseConversationAction } from 'src/stores/clients';
import { getSocket } from 'src/configs/socket-config';
import { 
  DownloadOutlined, 
  MessageOutlined, 
  FileTextOutlined,
  BarChartOutlined,
  CalendarOutlined,
  RobotOutlined,
  FileSearchOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { Dropdown, Menu } from 'antd';
import jsPDF from 'jspdf';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Alert } from 'antd';
import dayjs from 'dayjs';
import { GeneratedDocument } from 'src/interfaces/document-interface';
import GeneratedDocumentView from '../sessions/components/GeneratedDocument';
import { BaseDocumentType } from 'src/pages/clients/client-details-page-constants';
import AIConversationView from 'src/components/ai-conversation/AIConversationView';
import { useLocation, useSearchParams } from 'react-router-dom';

// Define interface for location state
interface LocationState {
  reload?: boolean;
  highlightMessage?: string;
  classification?: string;
}

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// Define the document types for AI summaries
const AI_DOCUMENT_TYPES: BaseDocumentType[] = [
  {
    type: 'summary',
    title: 'AI Summary',
    description: 'AI-generated summary of selected conversations',
    category: 'Analysis',
    icon: BarChartOutlined
  }
];

const AITab: FC<{ clientId: string }> = ({ clientId }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [conversations, setConversations] = useState<IAIConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<IAIConversation | null>(null);
  const [selectedConversationIds, setSelectedConversationIds] = useState<string[]>([]);
  const [summaryData, setSummaryData] = useState<string | null>(null);
  const [loadingSummary, setLoadingSummary] = useState<boolean>(false);
  const socket = getSocket();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('conversations');
  const [documents, setDocuments] = useState<GeneratedDocument[]>([]);
  const [generatingDocumentIds, setGeneratingDocumentIds] = useState<string[]>([]);
  
  // Initialize with a more reasonable date range - last 30 days to today
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([
    dayjs().subtract(30, 'days').startOf('day'),
    dayjs().add(1, 'day').endOf('day') // Add one extra day to ensure all conversations are captured
  ]);
  
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [highlightedClassification, setHighlightedClassification] = useState<string | null>(null);

  // Load initial conversations
  useEffect(() => {
    // Check for URL parameters first
    const queryParams = new URLSearchParams(window.location.search);
    const messageIdParam = queryParams.get('messageId');
    const conversationIdParam = queryParams.get('conversationId');
    
    // If we have URL parameters, handle them specifically
    if (messageIdParam || conversationIdParam) {
      handleUrlParameters(messageIdParam, conversationIdParam);
      return;
    }
    
    // Otherwise, load conversations normally
    const fetchInitialConversations = async () => {
      setLoading(true);
      try {
        const startDate = dateRange[0];
        const endDate = dateRange[1];
        
        if (startDate && endDate) {
          console.log("AITab: Initial fetch with date range:", {
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD')
          });
          
          const result = await dispatch(getAIConversationsAction({ 
            clientId,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
          })).unwrap();
          
          console.log(`AITab: Initial fetch returned ${result.conversations.length} conversations`);
          setConversations(result.conversations);
        }
      } catch (error) {
        console.error("Error fetching initial conversations:", error);
        showErrorToast('Failed to load AI conversations');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialConversations();
  }, [clientId, dateRange, dispatch]);

  // Handle URL parameters separately
  const handleUrlParameters = async (messageIdParam: string | null, conversationIdParam: string | null) => {
    if (messageIdParam) {
      console.log(`Found messageId in URL: ${messageIdParam}`);
      setHighlightedMessageId(messageIdParam);
      setHighlightedClassification("flagged");
      
      // Fetch conversations to find the message
      await fetchConversationsAndOpenMessage(messageIdParam);
    } else if (conversationIdParam) {
      console.log(`Found conversationId in URL: ${conversationIdParam}`);
      
      // Check if we have a message to highlight in location state
      const locationState = location.state as LocationState | null;
      const highlightMessageId = locationState?.highlightMessage || null;
      const messageClassification = locationState?.classification || "flagged";
      
      if (highlightMessageId) {
        setHighlightedMessageId(highlightMessageId);
        setHighlightedClassification(messageClassification);
        console.log(`Setting message highlight: ${highlightMessageId} as ${messageClassification}`);
      }
      
      await fetchConversationsAndOpenConversation(conversationIdParam);
    }
    
    // Clean up URL parameters
    const newParams = new URLSearchParams(window.location.search);
    newParams.delete('messageId');
    newParams.delete('conversationId');
    const newUrl = window.location.pathname + (newParams.toString() ? `?${newParams.toString()}` : '');
    window.history.replaceState({}, '', newUrl);
  };

  const fetchConversationsAndOpenMessage = async (messageId: string) => {
    setLoading(true);
    try {
      const result = await dispatch(getAIConversationsAction({ 
        clientId,
        startDate: dateRange[0]?.toISOString(),
        endDate: dateRange[1]?.toISOString()
      })).unwrap();
      
      setConversations(result.conversations);
      
      // Find the conversation containing the message
      const conversation = result.conversations.find(conv => 
        conv.messages.some((msg: { id: string }) => msg.id === messageId)
      );
      
      if (conversation) {
        console.log(`Found conversation containing message ${messageId}`, conversation);
        await openConversationWithFullMessages(conversation);
      } else {
        console.log(`Could not find conversation with message ${messageId}`);
        showErrorToast('Could not find the requested conversation');
      }
    } catch (error) {
      console.error("Error fetching conversations for message:", error);
      showErrorToast('Failed to load AI conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversationsAndOpenConversation = async (conversationId: string) => {
    setLoading(true);
    try {
      const result = await dispatch(getAIConversationsAction({ 
        clientId,
        startDate: dateRange[0]?.toISOString(),
        endDate: dateRange[1]?.toISOString()
      })).unwrap();
      
      setConversations(result.conversations);
      
      // Find the conversation by ID
      const conversation = result.conversations.find(conv => 
        conv.id === conversationId || conv.homeworkAssignId === conversationId
      );
      
      if (conversation) {
        console.log(`Found conversation with ID ${conversationId}`, conversation);
        await openConversationWithFullMessages(conversation);
      } else {
        // Try to fetch the conversation directly
        await fetchConversationDirectly(conversationId);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
      showErrorToast('Failed to load AI conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversationDirectly = async (conversationId: string) => {
    try {
      const result = await dispatch(getAiConversationAction({
        clientId,
        homeworkAssignId: conversationId
      })).unwrap();
      
      if (result && result.length > 0) {
        const tempConversation: IAIConversation = {
          id: conversationId,
          homeworkAssignId: conversationId,
          createdAt: result[0]?.createdAt || new Date().toISOString(),
          messages: result
        };
        setSelectedConversation(tempConversation);
        setIsModalVisible(true);
      } else {
        showErrorToast('Could not find the requested conversation');
      }
    } catch (error) {
      console.error("Error fetching conversation directly:", error);
      showErrorToast('Failed to load conversation details');
    }
  };

  const openConversationWithFullMessages = async (conversation: IAIConversation) => {
    setSelectedConversation(conversation);
    setIsModalVisible(true);
    setLoadingConversation(true);
    
    try {
      const result = await dispatch(getAiConversationAction({
        clientId,
        homeworkAssignId: conversation.homeworkAssignId
      })).unwrap();
      
      // Update with full message details including classification
      setSelectedConversation({
        ...conversation,
        messages: result
      });
      
      // Update classification for highlighted message if needed
      if (highlightedMessageId) {
        const highlightedMessage = result.find((msg: { id: string; classification?: string }) => 
          msg.id === highlightedMessageId
        );
        if (highlightedMessage && highlightedMessage.classification) {
          setHighlightedClassification(highlightedMessage.classification);
        }
      }
    } catch (error) {
      console.error("Error fetching full conversation:", error);
      showErrorToast('Failed to load conversation details');
    } finally {
      setLoadingConversation(false);
    }
  };

  // Handle selecting a conversation
  const handleSelectConversation = async (conversation: IAIConversation) => {
    setIsModalVisible(true);
    setSelectedConversation({
      ...conversation,
      messages: []
    });
    setLoadingConversation(true);
    
    try {
      const result = await dispatch(getAiConversationAction({
        clientId,
        homeworkAssignId: conversation.homeworkAssignId
      })).unwrap();
      
      // If we have a highlighted message ID, check if it's in this conversation
      if (highlightedMessageId) {
        const containsHighlightedMessage = result.some(
          (msg: { id: string }) => msg.id === highlightedMessageId
        );
        
        // If not found, clear the highlighted message state
        if (!containsHighlightedMessage) {
          setHighlightedMessageId(null);
          setHighlightedClassification(null);
        }
      }
      
      setSelectedConversation({
        ...conversation,
        messages: result
      });
    } catch (error) {
      showErrorToast('Failed to load conversation details');
      console.error('Error fetching conversation:', error);
    } finally {
      setLoadingConversation(false);
    }
  };

  // Modified summary handler
  const handleSummariseConversations = async () => {
    if (!clientId || selectedConversationIds.length === 0) {
      showErrorToast('Please select at least one conversation to summarise');
      return;
    }

    // Get the selected conversations
    const selectedConvos = conversations.filter(conv => 
      selectedConversationIds.includes(conv.homeworkAssignId)
    );

    // Create a mapping of conversation IDs to their dates
    const conversationDates = selectedConvos.reduce((acc, conv) => ({
      ...acc,
      [conv.homeworkAssignId]: conv.createdAt
    }), {});

    // Create a placeholder document
    const placeholderDocument: GeneratedDocument = {
      id: `summary_${Date.now()}`,
      content: "Generating summary...",
      documentType: 'summary',
      generatedAt: new Date().toISOString(),
      metadata: {
        wordCount: 0,
        estimatedReadTime: 0,
        transcriptIds: selectedConversationIds,
        transcriptDates: conversationDates
      }
    };

    // Add the placeholder and switch tabs
    setDocuments(prev => [placeholderDocument, ...prev]);
    setGeneratingDocumentIds(prev => [...prev, placeholderDocument.id]);
    setActiveTab('summaries');
    setLoadingSummary(true);

    socket.connectGPT(clientId);
    let newSummary = '';

    return new Promise<void>((resolve, reject) => {
      socket.listenGptResChunk((message) => {
        if (message.content === '[END]') {
          setLoadingSummary(false);
          socket.offGptResChunk();
          
          // Calculate word count and read time
          const wordCount = newSummary.split(/\s+/).length;
          const estimatedReadTime = Math.ceil(wordCount / 200); // Assuming 200 words per minute

          // Update the placeholder with the real summary
          setDocuments(prev => prev.map(doc => 
            doc.id === placeholderDocument.id 
              ? { 
                  ...doc, 
                  content: newSummary,
                  metadata: {
                    ...doc.metadata,
                    wordCount,
                    estimatedReadTime
                  }
                }
              : doc
          ));
          setGeneratingDocumentIds(prev => prev.filter(id => id !== placeholderDocument.id));
          resolve();
        } else if (message.content === '[ERROR]') {
          setLoadingSummary(false);
          socket.offGptResChunk();
          // Remove the placeholder on error
          setDocuments(prev => prev.filter(doc => doc.id !== placeholderDocument.id));
          setGeneratingDocumentIds(prev => prev.filter(id => id !== placeholderDocument.id));
          showErrorToast('Error generating summary');
          reject(new Error('Failed to generate summary'));
        } else {
          newSummary += message.content;
          setSummaryData(newSummary);
        }
      });

      dispatch(
        summariseConversationAction({
          clientId,
          homeworkAssignIds: selectedConversationIds
        })
      );
    });
  };

  // Close modal callback
  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedConversation(null);
    setHighlightedMessageId(null);
    setHighlightedClassification(null);
  };

  // Add handler for checkbox selection
  const handleConversationSelect = (conversationId: string) => {
    setSelectedConversationIds(prev => 
      prev.includes(conversationId)
        ? prev.filter(id => id !== conversationId)
        : [...prev, conversationId]
    );
  };

  const conversationColumns: ColumnsType<IAIConversation> = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a: IAIConversation, b: IAIConversation) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      render: (date: string) => dayjs(date).format('MMMM D, YYYY'),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record: IAIConversation) => 
        title || `Conversation from ${dayjs(record.createdAt).format('MMMM D, YYYY')}`,
    },
    {
      title: 'Messages',
      dataIndex: 'messageCount',
      key: 'messageCount',
      render: (_: unknown, record: IAIConversation) => record.messages?.length || 0,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: IAIConversation) => (
        <Space size="middle" className="AITab__actions">
          <Tooltip title="Select for summary generation">
            <div className="AITab__checkboxWrapper">
              <Checkbox
                checked={selectedConversationIds.includes(record.homeworkAssignId)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleConversationSelect(record.homeworkAssignId);
                }}
              >
                Include in summary
              </Checkbox>
            </div>
          </Tooltip>
          <Tooltip title="View conversation details">
            <Button
              type="primary"
              icon={<MessageOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setIsModalVisible(true);
                handleSelectConversation(record);
              }}
            >
              View Conversation
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Show loading state
  if (loading) {
    return (
      <div className="AITab" style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="AITab">
      <div className="AITab__introSection" style={{ marginBottom: '24px' }}>
        <h2 style={{ marginBottom: '12px' }}>AI Conversation History</h2>
        <p style={{ marginBottom: '16px' }}>
          This tab shows all conversations between your client and our AI assistant, jAImee. You can:
          <ul>
            <li>View past conversations and their complete chat history</li>
            <li>Generate summaries of one or multiple conversations</li>
            <li>Filter conversations by date range</li>
          </ul>
        </p>
      </div>

      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        className="AITab__tabs"
      >
        <TabPane 
          tab={
            <span>
              <RobotOutlined /> Conversations
            </span>
          } 
          key="conversations"
        >
          <div className="AITab__header">
            <div className="AITab__headerLeft">
              <BaseText type="caption">
                Filter conversations by date range 
                <Tooltip title="Date filters include conversations that occurred on or between the selected dates (inclusive). By default, shows the last 30 days of conversations.">
                  <span style={{ cursor: 'help', marginLeft: '4px' }}>(ℹ️)</span>
                </Tooltip>
                {(dateRange[0] || dateRange[1]) && 
                  <Tooltip title="Clear date filter">
                    <Button 
                      type="link" 
                      size="small"
                      onClick={() => {
                        // Set a reasonable default date range (last 30 days)
                        const startDate = dayjs().subtract(30, 'days').startOf('day');
                        const endDate = dayjs().add(1, 'day').endOf('day');
                        
                        setDateRange([startDate, endDate]);
                        
                        // Immediately fetch conversations with this range
                        dispatch(getAIConversationsAction({ 
                          clientId,
                          startDate: startDate.toISOString(),
                          endDate: endDate.toISOString()
                        })).then((action) => {
                          if (action.type.endsWith('/fulfilled')) {
                            const payload = action.payload as TGetAIConversationsResponse;
                            setConversations(payload.conversations);
                            console.log(`Reset filter: Fetched ${payload.conversations.length} conversations`);
                          }
                        });
                      }}
                      style={{ marginLeft: '8px', padding: '0' }}
                    >
                      (Reset)
                    </Button>
                  </Tooltip>
                }
              </BaseText>
              <RangePicker
                placeholder={['Start Date', 'End Date']}
                onChange={(dates) => {
                  if (dates) {
                    const startDate = dates[0]?.startOf('day') || null;
                    const endDate = dates[1] ? dates[1].add(1, 'day').endOf('day') : null;
                    
                    // Update the date range state
                    setDateRange([startDate, endDate]);
                    
                    // Immediately fetch conversations with this range
                    if (startDate && endDate) {
                      setLoading(true);
                      dispatch(getAIConversationsAction({ 
                        clientId,
                        startDate: startDate.toISOString(),
                        endDate: endDate.toISOString()
                      })).then((action) => {
                        if (action.type.endsWith('/fulfilled')) {
                          const payload = action.payload as TGetAIConversationsResponse;
                          setConversations(payload.conversations);
                          console.log(`Date range changed: Fetched ${payload.conversations.length} conversations`);
                        }
                        setLoading(false);
                      }).catch(() => {
                        setLoading(false);
                        showErrorToast('Failed to load AI conversations');
                      });
                    }
                  } else {
                    // Clear filter case - set to default range of 30 days
                    const startDate = dayjs().subtract(30, 'days').startOf('day');
                    const endDate = dayjs().add(1, 'day').endOf('day');
                    
                    setDateRange([startDate, endDate]);
                    
                    // Immediately fetch conversations with default range
                    setLoading(true);
                    dispatch(getAIConversationsAction({ 
                      clientId,
                      startDate: startDate.toISOString(),
                      endDate: endDate.toISOString()
                    })).then((action) => {
                      if (action.type.endsWith('/fulfilled')) {
                        const payload = action.payload as TGetAIConversationsResponse;
                        setConversations(payload.conversations);
                        console.log(`Date range cleared: Fetched ${payload.conversations.length} conversations`);
                      }
                      setLoading(false);
                    }).catch(() => {
                      setLoading(false);
                      showErrorToast('Failed to load AI conversations');
                    });
                  }
                }}
                className="AITab__datePicker"
                value={dateRange}
                allowClear={false}
              />
            </div>
            <div className="AITab__headerRight">
              <Button
                type="primary"
                onClick={handleSummariseConversations}
                loading={loadingSummary}
                disabled={selectedConversationIds.length === 0}
                icon={<BarChartOutlined />}
                size="middle"
                className="AITab__actionButton"
              >
                Generate Summary ({selectedConversationIds.length} selected)
              </Button>
            </div>
          </div>

          <Table
            columns={conversationColumns}
            dataSource={conversations}
            rowKey="id"
            className="AITab__table"
            loading={loading}
            pagination={{
              pageSize: 10,
              showTotal: (total) => `Total ${total} conversations`,
            }}
          />
        </TabPane>

        <TabPane 
          tab={
            <span>
              <FileSearchOutlined /> Summaries ({documents.length})
            </span>
          } 
          key="summaries"
        >
          <GeneratedDocumentView
            documents={documents}
            documentTypes={AI_DOCUMENT_TYPES}
            onGenerateDocument={handleSummariseConversations}
            isGenerating={loadingSummary}
            transcriptId={selectedConversationIds.join(',')}
            generatingDocumentIds={generatingDocumentIds}
          />
        </TabPane>
      </Tabs>

      <Modal
        title={selectedConversation?.title || "Conversation Details"}
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={
          <div className="AITab__modalFooter">
            <Space>
              <Button onClick={handleModalClose}>
                Close
              </Button>
            </Space>
          </div>
        }
        width={1020}
        className="AITab__modal"
      >
        {loadingConversation ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Spin size="large" tip="Loading conversation..." />
          </div>
        ) : selectedConversation && selectedConversation.messages ? (
          <div className="AITab__conversationContainer">
            {highlightedMessageId && highlightedClassification && (
              <Alert
                message={`This conversation contains a message classified as "${highlightedClassification}"`}
                description="Scroll down to view the flagged message highlighted below."
                type="warning"
                showIcon
                closable
                style={{ marginBottom: '20px' }}
              />
            )}
            <AIConversationView
              conversationData={selectedConversation.messages}
              clientName="You"
              conversationDate={selectedConversation.createdAt}
              showActions={false}
              highlightedMessageId={highlightedMessageId}
              highlightedClassification={highlightedClassification}
            />
          </div>
        ) : (
          <div className="AITab__loadingState">
            <Spin size="large" />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AITab; 